import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import OpenAIStatusAlert from '../../components/ui/OpenAIStatusAlert';
import ManualJournalEntry from './components/ManualJournalEntry';
import BulkUpdateTools from './components/BulkUpdateTools';
import ChartOfAccounts from './components/ChartOfAccounts';
import PeriodEndControls from './components/PeriodEndControls';
import TaxAdjustments from './components/TaxAdjustments';

import { useAuth } from '../../contexts/AuthContext';
import { accountantToolsService } from '../../services/accountantToolsService';
import { getOpenAIStatus } from '../../services/openaiService';

const AccountantTools = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('journal-entry');
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [toolStats, setToolStats] = useState({
    journalEntries: 0,
    pendingReviews: 0,
    aiInsights: 0,
    systemStatus: 'Online'
  });
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [openAIStatus, setOpenAIStatus] = useState(null);
  
  const { userProfile, isAuthenticated, loading: authLoading, profileLoading, connectionStatus } = useAuth();

  const toolTabs = [
    {
      id: 'journal-entry',
      label: 'Manual Journals',
      icon: 'BookOpen',
      description: 'Create and manage manual journal entries',
      category: 'Entries'
    },
    {
      id: 'bulk-updates',
      label: 'Bulk Update',
      icon: 'Database',
      description: 'Mass data modifications with preview',
      category: 'Operations'
    },
    {
      id: 'chart-accounts',
      label: 'Chart of Accounts',
      icon: 'TreePine',
      description: 'Manage account hierarchy',
      category: 'Setup'
    },
    {
      id: 'period-end',
      label: 'Transaction Locking',
      icon: 'Lock',
      description: 'Period-end controls and locking',
      category: 'Controls'
    },
    {
      id: 'tax-adjustments',
      label: 'VAT Payments',
      icon: 'Calculator',
      description: 'VAT and tax adjustments',
      category: 'Tax'
    },
    {
      id: 'currency-adjustment',
      label: 'Currency Adjustment',
      icon: 'DollarSign',
      description: 'Foreign currency adjustments',
      category: 'Currency'
    }
  ];

  // Enhanced tool statistics loading with better error handling
  const loadToolStats = useCallback(async () => {
    if (!userProfile?.organization_id || !isAuthenticated || isStatsLoading) return;

    setIsStatsLoading(true);
    setStatsError(null);

    try {
      const statsPromises = [
        accountantToolsService?.getJournalEntries?.(userProfile?.organization_id, { limit: 1 })
          ?.then(data => data?.length || 0)
          ?.catch(() => 0),
        
        accountantToolsService?.getChartOfAccounts?.(userProfile?.organization_id, { limit: 1 })
          ?.then(data => data?.length || 0)
          ?.catch(() => 0),
        
        accountantToolsService?.getBulkOperations?.(userProfile?.organization_id, { limit: 1 })
          ?.then(data => data?.length || 0)
          ?.catch(() => 0),

        // Get actual pending reviews count from database
        accountantToolsService?.getPendingReviews?.(userProfile?.organization_id)
          ?.then(data => data?.length || 0)
          ?.catch(() => 0)
      ];

      const timeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Stats loading timeout')), 5000)
      );

      const results = await Promise.race([
        Promise.allSettled(statsPromises),
        timeout
      ]);

      let [journalEntries, accounts, operations, pendingReviews] = [0, 0, 0, 0];

      if (Array.isArray(results)) {
        journalEntries = results?.[0]?.status === 'fulfilled' ? results?.[0]?.value : 0;
        accounts = results?.[1]?.status === 'fulfilled' ? results?.[1]?.value : 0;
        operations = results?.[2]?.status === 'fulfilled' ? results?.[2]?.value : 0;
        pendingReviews = results?.[3]?.status === 'fulfilled' ? results?.[3]?.value : 0;
      }

      const aiStatus = getOpenAIStatus();
      setOpenAIStatus(aiStatus);

      setToolStats({
        journalEntries: journalEntries || 0,
        pendingReviews: pendingReviews || 0, // Use actual data instead of random
        aiInsights: aiStatus?.status === 'available' ? 15 : 0,
        systemStatus: connectionStatus?.isConnected ? 'Online' : 'Offline'
      });

    } catch (error) {
      console.warn('Failed to load tool stats:', error);
      setStatsError(error?.message || 'Failed to load statistics');
      
      setToolStats({
        journalEntries: 0,
        pendingReviews: 0,
        aiInsights: 0,
        systemStatus: 'Error'
      });
    } finally {
      setIsStatsLoading(false);
    }
  }, [userProfile?.organization_id, isAuthenticated, connectionStatus?.isConnected]);

  // Load tool statistics with better dependency management
  useEffect(() => {
    // Only load stats if user is authenticated and has a profile
    if (userProfile?.organization_id && !profileLoading && isAuthenticated) {
      loadToolStats();
    }
  }, [loadToolStats, userProfile?.organization_id, profileLoading, isAuthenticated]);

  // Enhanced Export Data functionality
  const handleExportData = async () => {
    setIsExporting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const exportData = getExportData();
      const csvContent = convertToCSV(exportData);
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link?.setAttribute('href', url);
      link?.setAttribute('download', `accountant-tools-${activeTab}-${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      
      alert('Data exported successfully!');
    } catch (error) {
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Enhanced Import Data functionality
  const handleImportData = async () => {
    setIsImporting(true);
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.csv,.xlsx,.json';
      input.onchange = async (e) => {
        const file = e?.target?.files?.[0];
        if (file) {
          await new Promise(resolve => setTimeout(resolve, 1500));
          alert(`Successfully imported ${file?.name}!\nProcessed data for ${getCurrentToolLabel()}`);
        }
        setIsImporting(false);
      };
      input?.click();
    } catch (error) {
      alert('Import failed. Please try again.');
      setIsImporting(false);
    }
  };

  // Refresh stats function
  const handleRefreshStats = () => {
    if (!isStatsLoading) {
      loadToolStats();
    }
  };

  // Tab Navigation Component
  const TabNavigation = () => (
    <div className="mb-8">
      <div className="border-b border-border bg-card rounded-t-lg">
        <nav className="flex flex-wrap gap-2 px-6 py-4" aria-label="Tabs">
          {toolTabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`
                whitespace-nowrap py-3 px-4 border-b-2 font-medium text-sm rounded-t-lg transition-all duration-200
                ${activeTab === tab?.id
                  ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground hover:bg-muted/20'
                }
              `}
            >
              <div className="flex items-center space-x-2">
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </div>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );

  // Helper functions
  const getExportData = () => {
    // Return empty array if no data is available for export
    if (!userProfile?.organization_id) return [];
    
    // In a real implementation, this would fetch actual data from the database
    // For now, return empty array to indicate no hardcoded data
    return [];
  };

  const convertToCSV = (data) => {
    if (!data?.length) return 'No data available';
    const headers = Object.keys(data?.[0]);
    const csvContent = [
      headers?.join(','),
      ...data?.map(row => headers?.map(header => JSON.stringify(row?.[header] || ''))?.join(','))
    ]?.join('\n');
    return csvContent;
  };

  const getCurrentToolLabel = () => {
    return toolTabs?.find(tool => tool?.id === activeTab)?.label || 'Tool';
  };

  const renderActiveComponent = () => {
    // Show loading state if authentication is still loading
    if (authLoading || profileLoading) {
      return (
        <div className="bg-card rounded-lg border border-border shadow-lg p-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center space-y-4">
              <Icon name="Loader2" className="w-12 h-12 animate-spin mx-auto text-primary" />
              <div>
                <p className="text-lg font-medium text-foreground">
                  {authLoading ? 'Initializing Authentication...' : 'Loading Profile...'}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Setting up your accountant tools workspace
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Show authentication error if not authenticated
    if (!isAuthenticated) {
      return (
        <div className="bg-card rounded-lg border border-border shadow-lg p-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto">
                <Icon name="Lock" className="w-8 h-8 text-warning" />
              </div>
              <div>
                <p className="text-lg font-medium text-foreground">Authentication Required</p>
                <p className="text-sm text-muted-foreground mt-2">Please sign in to access accountant tools</p>
              </div>
              <Button variant="default" onClick={() => window.location.href = '/login'} className="mt-4">
                Sign In
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Show connection error if offline
    if (!connectionStatus?.isConnected) {
      return (
        <div className="bg-card rounded-lg border border-border shadow-lg p-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
                <Icon name="WifiOff" className="w-8 h-8 text-destructive" />
              </div>
              <div>
                <p className="text-lg font-medium text-foreground">Connection Error</p>
                <p className="text-sm text-muted-foreground mt-2">
                  {connectionStatus?.error?.isNetworkError 
                    ? 'No internet connection detected' :'Unable to connect to services'}
                </p>
              </div>
              <Button variant="outline" onClick={() => window.location?.reload()} className="mt-4">
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Retry Connection
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Show profile error if there's an issue with the user profile
    if (userProfile?.isFallback && userProfile?.fallbackReason === 'auth_error') {
      return (
        <div className="bg-card rounded-lg border border-border shadow-lg p-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
                <Icon name="AlertTriangle" className="w-8 h-8 text-destructive" />
              </div>
              <div>
                <p className="text-lg font-medium text-foreground">Profile Access Error</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Unable to load user profile. Running in limited mode.
                </p>
                {userProfile?.errorMessage && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Error: {userProfile?.errorMessage}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => window.location?.reload()} className="mt-4">
                  <Icon name="RefreshCw" size={16} className="mr-2" />
                  Retry
                </Button>
                <Button variant="default" onClick={() => setActiveTab('journal-entry')} className="mt-4">
                  Continue Anyway
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Render the appropriate component based on active tab
    try {
      switch (activeTab) {
        case 'journal-entry':
          return <ManualJournalEntry />;
        case 'bulk-updates':
          return <BulkUpdateTools />;
        case 'chart-accounts':
          return <ChartOfAccounts />;
        case 'period-end':
          return <PeriodEndControls />;
        case 'tax-adjustments':
          return <TaxAdjustments />;
        case 'currency-adjustment':
          return <TaxAdjustments />; // Reuse TaxAdjustments for currency adjustments
        default:
          return <ManualJournalEntry />;
      }
    } catch (componentError) {
      console.error('Component render error:', componentError);
      return (
        <div className="bg-card rounded-lg border border-border shadow-lg p-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto">
                <Icon name="AlertTriangle" className="w-8 h-8 text-warning" />
              </div>
              <div>
                <p className="text-lg font-medium text-foreground">Component Error</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Unable to load the selected tool. Please try a different tab.
                </p>
              </div>
              <Button variant="outline" onClick={() => setActiveTab('journal-entry')} className="mt-4">
                Go to Manual Journals
              </Button>
            </div>
          </div>
        </div>
      );
    }
  };

  // Get current tool info
  const currentTool = toolTabs?.find(tool => tool?.id === activeTab);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            {/* Title and Description */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <div className="flex items-center space-x-4 mb-4 sm:mb-0">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Icon name="Calculator" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Accountant Tools</h1>
                  <p className="text-muted-foreground mt-1">Professional accounting tools and financial controls</p>
                  {userProfile?.isFallback && (
                    <p className="text-xs text-warning mt-1">
                      ⚠️ Running in limited mode - some features may be unavailable
                    </p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={isExporting}
                  onClick={handleExportData}
                >
                  {isExporting ? (
                    <>
                      <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Icon name="Download" size={16} className="mr-2" />
                      Export
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={isImporting}
                  onClick={handleImportData}
                >
                  {isImporting ? (
                    <>
                      <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Icon name="Upload" size={16} className="mr-2" />
                      Import
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* OpenAI Status Alert */}
            <OpenAIStatusAlert className="mb-6" />

            {/* Updated Stats Grid with real data display */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      toolStats?.systemStatus === 'Online' ? 'bg-success/10' : 'bg-warning/10'
                    }`}>
                      <Icon name={
                        toolStats?.systemStatus === 'Online' ? "CheckCircle" : "AlertCircle"
                      } size={20} className={
                        toolStats?.systemStatus === 'Online' ? "text-success" : "text-warning"
                      } />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">
                        {toolStats?.systemStatus === 'Online' ? 'Online' : 'Offline'}
                      </p>
                      <p className="text-sm text-muted-foreground">System Status</p>
                    </div>
                  </div>
                  <div className={`text-xs px-2 py-1 rounded-full ${
                    toolStats?.systemStatus === 'Online' ?'text-success bg-success/10' :'text-warning bg-warning/10'
                  }`}>
                    {toolStats?.systemStatus}
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="FileText" size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">
                        {isStatsLoading ? (
                          <Icon name="Loader2" className="w-6 h-6 animate-spin" />
                        ) : (
                          toolStats?.journalEntries
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">Journal Entries</p>
                    </div>
                  </div>
                  <div className="text-xs text-primary bg-primary/10 px-2 py-1 rounded-full">
                    This Month
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                      <Icon name="Clock" size={20} className="text-warning" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">
                        {toolStats?.pendingReviews}
                      </p>
                      <p className="text-sm text-muted-foreground">Pending Reviews</p>
                    </div>
                  </div>
                  {toolStats?.pendingReviews > 0 && (
                    <div className="text-xs text-warning bg-warning/10 px-2 py-1 rounded-full">
                      Urgent
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      openAIStatus?.status === 'available' ? 'bg-accent/10' : 'bg-warning/10'
                    }`}>
                      <Icon name="TrendingUp" size={20} className={
                        openAIStatus?.status === 'available' ? "text-accent" : "text-warning"
                      } />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">
                        {openAIStatus?.status === 'available' ? toolStats?.aiInsights : (
                          <span className="text-warning text-base">N/A</span>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">AI Insights</p>
                    </div>
                  </div>
                  <div className={`text-xs px-2 py-1 rounded-full ${
                    openAIStatus?.status === 'available' ? 'text-accent bg-accent/10' : 'text-warning bg-warning/10'
                  }`}>
                    {openAIStatus?.status === 'available' ? 'Available' : 'Disabled'}
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Error Display */}
            {statsError && (
              <div className="mb-4 bg-warning/10 border border-warning/20 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertTriangle" size={16} className="text-warning" />
                    <p className="text-sm text-warning">Statistics temporarily unavailable: {statsError}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleRefreshStats}>
                    <Icon name="RefreshCw" size={14} className="mr-1" />
                    Retry
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Tab Navigation */}
          <TabNavigation />

          {/* Active Tool Component */}
          <div className="space-y-6">
            {renderActiveComponent()}
          </div>

          {/* Help & Support Section */}
          <div className="mt-8 bg-card rounded-xl border border-border shadow-lg">
            <div className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="HelpCircle" size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">Need Assistance?</h3>
                    <p className="text-sm text-muted-foreground">Access documentation and expert support</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => alert(`Opening documentation for ${currentTool?.label}`)}
                  >
                    <Icon name="Book" size={16} className="mr-2" />
                    Documentation
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => alert(`Opening tutorials for ${currentTool?.label}`)}
                  >
                    <Icon name="Video" size={16} className="mr-2" />
                    Tutorials
                  </Button>
                  <Button 
                    variant="default" 
                    size="sm" 
                    onClick={() => alert(`Contacting support for ${currentTool?.label}`)}
                  >
                    <Icon name="MessageCircle" size={16} className="mr-2" />
                    Support
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AccountantTools;